import { FormEvent, useRef, useState } from "react";

// Formspree receives the submission and forwards it by email. The site itself
// stays static, so there is no endpoint of our own to verify a CAPTCHA token
// against; spam is handled by the _gotcha honeypot below plus Formspree's own
// filtering.
const FORMSPREE_ENDPOINT = "https://formspree.io/f/xoeqejjy";

type Status = "idle" | "sending" | "sent" | "error";

type ContactFormProps = {
  variant?: "default" | "compact";
  contextLabel?: string;
  contextPrompt?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  subjectLabel?: string;
};

export default function ContactForm({
  variant = "default",
  contextLabel = "Technical conversation",
  contextPrompt = "Give me the useful context.",
  messageLabel = "What's happening?",
  messagePlaceholder = "A few sentences about the system, the constraint, and the outcome you need.",
  subjectLabel = "Enquiry",
}: ContactFormProps = {}) {
  const formStartTracked = useRef(false);
  const [status, setStatus] = useState<Status>("idle");
  const [statusMessage, setStatusMessage] = useState("");

  function plausible(eventName: string) {
    const analyticsWindow = window as typeof window & {
      plausible?: (eventName: string) => void;
    };
    analyticsWindow.plausible?.(eventName);
  }

  function trackFormStart() {
    if (formStartTracked.current) return;
    formStartTracked.current = true;
    plausible("Contact Form Started");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") || "");

    setStatus("sending");
    setStatusMessage("Sending your enquiry…");

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name,
          email: data.get("email"),
          company: data.get("company"),
          message: data.get("message"),
          _gotcha: data.get("_gotcha"),
          _subject: `${subjectLabel} from ${name}`,
        }),
      });

      if (!response.ok) {
        const result = (await response.json().catch(() => null)) as {
          errors?: { message?: string }[];
        } | null;
        throw new Error(
          result?.errors?.[0]?.message || "The message could not be sent.",
        );
      }

      form.reset();
      formStartTracked.current = false;
      // Fired only on a confirmed delivery, so the metric counts enquiries
      // rather than submit clicks.
      plausible("Enquiry Submitted");
      setStatus("sent");
      setStatusMessage("Message sent. I’ll reply to your work email.");
    } catch (error) {
      setStatus("error");
      setStatusMessage(
        error instanceof Error
          ? error.message
          : "The message could not be sent. Please try again.",
      );
    }
  }

  return (
    <form className={`contact-form${variant === "compact" ? " contact-form-compact" : ""}`} onSubmit={handleSubmit} onFocusCapture={trackFormStart}>
      <div className="form-intro">
        {variant === "default" && <span>{contextLabel}</span>}
        <strong>{contextPrompt}</strong>
      </div>

      <div className="form-row">
        <label>
          Name
          <input name="name" type="text" autoComplete="name" required />
        </label>
        <label>
          Work email
          <input name="email" type="email" autoComplete="email" required />
        </label>
      </div>

      <label>
        Company <span>(optional)</span>
        <input name="company" type="text" autoComplete="organization" />
      </label>

      <label>
        {messageLabel}
        <textarea
          name="message"
          rows={variant === "compact" ? 5 : 6}
          placeholder={messagePlaceholder}
          required
        />
      </label>

      <label className="form-trap" aria-hidden="true">
        Website
        <input name="_gotcha" type="text" tabIndex={-1} autoComplete="off" />
      </label>

      <button className="form-submit" type="submit" disabled={status === "sending"}>
        {status === "sending" ? "Sending…" : "Send message"} <span aria-hidden="true">↗</span>
      </button>
      <p className={`form-privacy form-privacy-${status}`} aria-live="polite">
        {statusMessage ||
          "Sent via Formspree, which processes and forwards the message. Your work email is only used to reply."}
      </p>
    </form>
  );
}
